import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgClass } from '@angular/common';
import { DATE_PRESET_OPTIONS, DatePreset, DateRange, resolveDatePreset } from './date-presets';

/** A preset and the range it resolved to, emitted when a preset button is clicked. */
export interface DatePresetSelection extends DateRange {
  preset: DatePreset;
}

/**
 * Preset buttons that sit beside a filter bar's From/To dates: This week, Last week, This month, Last
 * month and This term. The host sets its own date controls from the emitted range, and should pass
 * `null` to `[selected]` once the user edits the dates by hand.
 *
 * Remember the preset rather than the dates, so a remembered "This week" is this week on the next
 * visit, not the week it was saved.
 */
@Component({
  selector: 'app-date-presets',
  standalone: true,
  imports: [NgClass],
  template: `
    <div class="date-presets" role="group" aria-label="Date presets">
      @for (option of options; track option.preset) {
        <button type="button" class="btn btn-outline-primary btn-sm"
          [ngClass]="{ 'active': selected === option.preset }"
          [attr.aria-pressed]="selected === option.preset"
          (click)="choose(option.preset)">{{ option.label }}</button>
      }
    </div>
  `,
  styles: [`
    .date-presets { display: flex; flex-wrap: wrap; gap: 4px; }
  `]
})
export class DatePresetsComponent {
  /** The active preset, or null when the dates were set by hand. */
  @Input() selected: DatePreset | null = null;
  @Output() presetSelected = new EventEmitter<DatePresetSelection>();

  public readonly options = DATE_PRESET_OPTIONS;

  /** Overridable in tests. */
  public today: () => Date = () => new Date();

  public choose(preset: DatePreset): void {
    this.selected = preset;
    this.presetSelected.emit({ preset, ...resolveDatePreset(preset, this.today()) });
  }
}
